import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Download, 
  Star, 
  TrendingUp, 
  Users, 
  BarChart3,
  ArrowUpRight,
  RefreshCw,
  Bell,
  Settings
} from 'lucide-react';

const DashboardHome = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [stats, setStats] = useState({
    totalTemplates: 3247,
    totalDownloads: 125430,
    averageRating: 4.7,
    activeUsers: 2840,
    monthlyGrowth: 12.5
  });

  const [recentTemplates, setRecentTemplates] = useState([
    { id: 1, name: 'ModernTech Pro', category: 'Modern', downloads: 1250, trend: 'up' },
    { id: 2, name: 'Classic Executive', category: 'Classic', downloads: 980, trend: 'up' },
    { id: 3, name: 'Creative Artist', category: 'Creative', downloads: 750, trend: 'down' }
  ]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Simulation de rafraîchissement des données
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsRefreshing(false);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const cardHoverVariants = {
    hover: {
      y: -4,
      scale: 1.02,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div 
      className="min-h-screen bg-gray-50"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Header avec animations */}
      <motion.header 
        className="bg-white border-b border-gray-200 px-6 py-4"
        variants={itemVariants}
      >
        <div className="flex items-center justify-between">
          <div>
            <motion.h1 
              className="text-h1 text-gray-900"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              Dashboard
            </motion.h1>
            <motion.p 
              className="text-caption"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Vue d'ensemble de votre plateforme
            </motion.p>
          </div>
          <div className="flex items-center space-x-4">
            <motion.button
              onClick={handleRefresh}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              disabled={isRefreshing}
            >
              <motion.div
                animate={{ rotate: isRefreshing ? 360 : 0 }}
                transition={{ duration: 1, repeat: isRefreshing ? Infinity : 0 }}
              >
                <RefreshCw className="h-5 w-5" />
              </motion.div>
            </motion.button>
            <motion.button
              className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <Bell className="h-5 w-5" />
            </motion.button>
            <motion.button
              className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <Settings className="h-5 w-5" />
            </motion.button>
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">Admin User</p>
                <p className="text-xs text-gray-500">admin@busol.com</p>
              </div>
              <motion.div 
                className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center"
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.2 }}
              >
                <span className="text-sm font-medium text-white">A</span>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Contenu principal */}
      <main className="p-6">
        {/* Statistiques principales avec animations */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          variants={containerVariants}
        >
          {[
            {
              title: 'Total Templates',
              value: stats.totalTemplates.toLocaleString(),
              icon: FileText,
              color: 'primary',
              change: `+${stats.monthlyGrowth}%`,
              changeText: 'ce mois'
            },
            {
              title: 'Téléchargements',
              value: stats.totalDownloads.toLocaleString(),
              icon: Download,
              color: 'green',
              change: '+8.2%',
              changeText: 'ce mois'
            },
            {
              title: 'Note moyenne',
              value: `${stats.averageRating}/5`,
              icon: Star,
              color: 'yellow',
              change: '2,847 avis',
              changeText: ''
            },
            {
              title: 'Utilisateurs actifs',
              value: stats.activeUsers.toLocaleString(),
              icon: Users,
              color: 'purple',
              change: '+15.3%',
              changeText: 'ce mois'
            }
          ].map((stat, index) => (
            <motion.div
              key={stat.title}
              className="bg-white rounded-2xl p-6 border border-gray-200"
              variants={itemVariants}
              whileHover="hover"
              custom={index}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-caption text-gray-600">{stat.title}</p>
                  <motion.p 
                    className="text-3xl font-bold text-gray-900"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    {stat.value}
                  </motion.p>
                </div>
                <motion.div 
                  className={`w-12 h-12 bg-${stat.color}-50 rounded-xl flex items-center justify-center`}
                  whileHover={{ rotate: 10 }}
                  transition={{ duration: 0.3 }}
                >
                  <stat.icon className={`h-6 w-6 text-${stat.color}-500`} />
                </motion.div>
              </div>
              <motion.div 
                className="flex items-center mt-4"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
              >
                <ArrowUpRight className="h-4 w-4 text-success-500 mr-1" />
                <span className="text-sm text-success-500 font-medium">{stat.change}</span>
                {stat.changeText && (
                  <span className="text-sm text-gray-500 ml-2">{stat.changeText}</span>
                )}
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        {/* Contenu en deux colonnes */}
        <motion.div 
          className="grid grid-cols-1 lg:grid-cols-3 gap-6"
          variants={containerVariants}
        >
          {/* Graphique des performances */}
          <motion.div 
            className="lg:col-span-2 bg-white rounded-2xl p-6 border border-gray-200"
            variants={itemVariants}
            whileHover={{ y: -2 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-h3 text-gray-900">Performances</h2>
              <div className="flex space-x-2">
                {['7j', '30j', '90j'].map((period, index) => (
                  <motion.button
                    key={period}
                    className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                      index === 0 
                        ? 'bg-primary-50 text-primary-600' 
                        : 'text-gray-500 hover:bg-gray-50'
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.6 + index * 0.1 }}
                  >
                    {period}
                  </motion.button>
                ))}
              </div>
            </div>
            <motion.div 
              className="h-64 bg-gray-50 rounded-xl flex items-center justify-center"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.8 }}
            >
              <div className="text-center">
                <motion.div
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                </motion.div>
                <p className="text-gray-500">Graphique des performances</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Templates récents */}
          <motion.div 
            className="bg-white rounded-2xl p-6 border border-gray-200"
            variants={itemVariants}
            whileHover={{ y: -2 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-h3 text-gray-900">Templates récents</h2>
              <motion.a
                href="/templates"
                className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Voir tout
              </motion.a>
            </div>
            <div className="space-y-4">
              <AnimatePresence>
                {recentTemplates.map((template, index) => (
                  <motion.div
                    key={template.id}
                    className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-xl transition-colors cursor-pointer"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    whileHover={{ x: 5 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <motion.div 
                      className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center"
                      whileHover={{ rotate: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <FileText className="h-5 w-5 text-gray-600" />
                    </motion.div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{template.name}</p>
                      <p className="text-xs text-gray-500">
                        {template.category} • {template.downloads} téléchargements
                      </p>
                    </div>
                    <motion.div
                      animate={{ 
                        y: template.trend === 'up' ? [0, -2, 0] : [0, 2, 0]
                      }}
                      transition={{ 
                        duration: 1.5, 
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      <TrendingUp className={`h-4 w-4 ${
                        template.trend === 'up' ? 'text-success-500' : 'text-red-500'
                      }`} />
                    </motion.div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>

        {/* Actions rapides */}
        <motion.div 
          className="mt-8 bg-white rounded-2xl p-6 border border-gray-200"
          variants={itemVariants}
          whileHover={{ y: -2 }}
          transition={{ duration: 0.3 }}
        >
          <h2 className="text-h3 text-gray-900 mb-6">Actions rapides</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                title: 'Gérer les templates',
                description: 'Voir et organiser vos templates',
                icon: FileText,
                color: 'primary',
                href: '/templates'
              },
              {
                title: 'Statistiques',
                description: 'Analyser les performances',
                icon: Download,
                color: 'green',
                href: '/analytics'
              },
              {
                title: 'Utilisateurs',
                description: 'Gérer les comptes',
                icon: Users,
                color: 'purple',
                href: '/users'
              }
            ].map((action, index) => (
              <motion.button
                key={action.title}
                className="flex items-center space-x-3 p-4 border border-gray-200 rounded-xl hover:border-primary-300 hover:bg-primary-50 transition-colors"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <motion.div 
                  className={`w-10 h-10 bg-${action.color}-50 rounded-lg flex items-center justify-center`}
                  whileHover={{ rotate: 10 }}
                  transition={{ duration: 0.3 }}
                >
                  <action.icon className={`h-5 w-5 text-${action.color}-500`} />
                </motion.div>
                <div className="text-left">
                  <p className="font-medium text-gray-900">{action.title}</p>
                  <p className="text-sm text-gray-500">{action.description}</p>
                </div>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </main>
    </motion.div>
  );
};

export default DashboardHome;
